import React from 'react';
import { useNavigate, useBlocker, NavigationType, type BlockerFunction } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import { SessionStatus, sessionAcknowledgePayment } from '@/store/slices/session';
import { useBeginCheckout, usePurchaseConversion } from '@/utils/analytics/useCheckoutConversions';
import type { PaymentAcknowledgement } from '@/utils/session-payment';
import { useResumePaidSession } from '@/nova/hooks/useResumePaidSession';
import { WizardLayout } from '../../components/wizard';
import { useConfigurator, WIZARD_STEPS } from '../../store/configurator';
import { DevicePreview } from '../finish/components/DevicePreview';
import { BillingSummary, BillingFooter } from '../finish/components/Billing';
import { isOneTimeBilling } from '../../data/billing';
import { PaymentPane, PaymentSuccess, type PaymentOutcome } from '../finish/components/payment';

type View = 'payment' | 'success';

/**
 * v0 Step 4 — Payment with Stripe. The checkout for the configured brand, as its own step.
 * The payment pane is rendered as the wizard overlay (covering the sidebar + footer, same
 * visual as v3's in-page checkout). "Back" returns to the review step; a successful payment
 * is confirmed server-side (the session reaches a post-payment status) and replaces the
 * whole screen with the confirmation.
 *
 * Reuses v3's `PaymentPane` / `PaymentSuccess` (copied under this flow's own folder). The
 * live checkout derives the real amount + buyer email off the session; the `amount`/`email`
 * props here only drive the display.
 */
export const PaymentStep = (): React.ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { emailAddress, billing } = useConfigurator();
  const [view, setView] = React.useState<View>('payment');

  // Payment success is confirmed server-side: the session reaches a post-payment status
  // (`payment_successful`, or `customization` once the backend advances it). Set by the WS
  // on the in-page flow and restored by `sessionLoad` on a full-page (3DS/redirect) return.
  const dispatch = useAppDispatch();
  const sessionStatus = useAppSelector((s) => s.session.status);
  const sessionId = useAppSelector((s) => s.session.sessionId);
  const invoiceStatus = useAppSelector((s) => s.session.invoiceStatus);

  // Which SCREEN shows: a session past checkout (`payment_successful`, or `customization` once
  // the backend advances it) lands on the confirmation. `customization` belongs HERE but NOT in
  // `settled` below: on the SPEI rail the acknowledge advances the session to `customization` on
  // the buyer's word, before money moves, so counting it as settlement would show "Payment
  // successful" the instant the transfer button was pressed.
  React.useEffect(() => {
    if (sessionStatus === SessionStatus.payment_successful || sessionStatus === SessionStatus.customization) {
      setView('success');
    }
  }, [sessionStatus]);

  // A session can arrive here with its invoice ALREADY PAID — a card charge whose webhook landed
  // after the buyer closed the tab, or a 3DS return. The backend leaves such a session on
  // `configuration` until it is told, so this acknowledges it (once) and hands back the verdict
  // the confirmation screen renders from. `checkedOut` also covers the resumed case, where the
  // buyer must land on the confirmation rather than a checkout they have already been through.
  const resume = useResumePaidSession();
  React.useEffect(() => {
    if (resume.checkedOut) setView('success');
  }, [resume.checkedOut]);

  /**
   * Stripe has taken the payment. Tell the backend, THEN show the confirmation.
   *
   * This call is what advances the session now: the backend no longer moves a session on by
   * itself when a charge lands, so without it the buyer sat on "Processing…" forever and the paid
   * session stayed stuck in `configuration`. `can_continue` is also what decides whether the
   * confirmation offers onboarding or asks the owner to check their inbox, so we await it — but a
   * failure still advances the screen (with a null verdict, which the screen falls back from)
   * rather than trapping someone whose card has already been charged.
   */
  // Latched the moment Stripe accepts, so the pane below stays mounted through the acknowledge.
  // `resolving` goes true again while that call is in flight (it is part of the same hook), and
  // without this the checkout would unmount mid-payment and blank the "Processing…" state.
  const [paying, setPaying] = React.useState(false);
  // The money has actually ARRIVED. Latched from the pane's outcome (a cleared card charge, or
  // nothing owed) and echoed by the payment evidence the store carries. Deliberately NOT gated on
  // `customization` (that only decides the SCREEN above, never a Purchase).
  const [settledNow, setSettledNow] = React.useState(false);
  const [acknowledgement, setAcknowledgement] = React.useState<PaymentAcknowledgement | null>(null);
  const settled = settledNow || sessionStatus === SessionStatus.payment_successful || invoiceStatus === 'paid';

  const onPaid = React.useCallback(async (outcome: PaymentOutcome): Promise<void> => {
    setPaying(true);
    setSettledNow(outcome.settled);
    setAcknowledgement(outcome.acknowledgement);
    // The shared pane already made the acknowledge call on the SPEI/shared rail and handed back
    // its verdict, so only acknowledge here when it did not (the Stripe-only surface).
    if (sessionId && outcome.acknowledgement == null) await dispatch(sessionAcknowledgePayment({ sessionId }));
    setView('success');
  }, [dispatch, sessionId]);

  // Conversion: InitiateCheckout / begin_checkout — the payment step IS the checkout in v0, so
  // fire once billing is known and the entry probe has answered: a session already past checkout
  // (paid invoice, or a post-payment status) opens on the confirmation, not on a checkout, so a
  // return to a paid session does not count a second start. No-ops without marketing consent.
  const pastCheckout = resume.checkedOut || sessionStatus === SessionStatus.customization;
  const beginCheckout = useBeginCheckout(billing, sessionId);
  React.useEffect(() => {
    if (billing && !resume.resolving && !pastCheckout) beginCheckout();
  }, [billing, resume.resolving, pastCheckout, beginCheckout]);

  // Conversion: Purchase (+ Subscribe for recurring monthly features) once the charge is
  // confirmed server-side. Once per session: dedups the WS success + a 3DS-reload return.
  usePurchaseConversion(settled, billing, sessionId);

  // Once payment succeeds the wizard is complete and its steps are stale (already paid), so
  // the browser Back button must not re-enter them. Block POP navigations on the confirmation
  // screen and re-arm the blocker so every subsequent attempt is blocked too.
  const backBlocker = useBlocker(
    React.useCallback<BlockerFunction>(
      ({ historyAction }) => view === 'success' && historyAction === NavigationType.Pop,
      [view],
    ),
  );
  React.useEffect(() => {
    if (backBlocker.state === 'blocked') backBlocker.reset();
  }, [backBlocker]);

  const symbol = billing?.symbol ?? '$';
  const currencyCode = billing?.currency ?? 'USD';
  const reviewPath = WIZARD_STEPS[2].path;

  if (view === 'success') {
    return <PaymentSuccess email={emailAddress} settled={settled} acknowledgement={acknowledgement ?? resume.acknowledgement} />;
  }

  return (
    <WizardLayout
      currentStep={3}
      title={t('Payment')}
      description={t('Enter your payment details to launch your brand.')}
      main={<DevicePreview />}
      footerExtra={
        <BillingFooter
          symbol={symbol}
          currencyCode={currencyCode}
          total={billing?.dueToday ?? null}
          oneTime={isOneTimeBilling(billing)}
        />
      }
      overlay={
        // Held back while the entry probe is still resolving. This step renders the pane
        // unconditionally, and the pane creates an invoice + loads Stripe the moment it mounts —
        // so without the gate a session that arrives already paid would start a second checkout
        // against a settled invoice before the probe could redirect to the confirmation.
        resume.resolving && !paying ? null : (
          <PaymentPane
            email={emailAddress}
            amount={billing?.dueToday ?? 0}
            grossAmount={billing?.total ?? 0}
            monthlyAmount={billing?.productFeatures ?? 0}
            currency={billing?.currency ?? 'USD'}
            currencySymbol={symbol}
            onBack={() => navigate(reviewPath)}
            onSuccess={(outcome) => void onPaid(outcome)}
          />
        )
      }
    >
      <BillingSummary billing={billing} symbol={symbol} currencyCode={currencyCode} />
    </WizardLayout>
  );
};

export default PaymentStep;
